/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */
import { ImageItem } from '@/utils/picControler/ImageBase';
import Mimes from '@/utils/picControler/mimes';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
// import { createImageList } from '../../utils/transform';
import { message } from '@sinohealth/butterfly-ui-components';
import useTransform from '../../hook/useTransform';
import style from './index.less';

interface ImageInputProps {
  multiple?: boolean; // 是否多选
  accept?: string; // 接受文件类型
  maxFiles?: number; // 总的最大数量
  surpluCount?: number; // 剩余的数量
  maxSize?: number; // 最大文件大小
  duration?: number; // 视频时长
  enableDrag?: boolean; // 是否启用拖拽上传功能
  enableFolderDrag?: boolean; // 是否支持文件夹拖拽（依赖 enableDrag 开启）
  dragTarget?: HTMLElement | null; // 拖拽目标元素
  onChange?: (files: Array<Promise<ImageItem>>, videoFile?: Array<File>) => void;
}

const acceptedMimes = Object.keys(Mimes)
  .map((item) => `.${item}`)
  .join(',');

/** 不在图片 Mimes 中的扩展名（音视频等），用于 MIME 与扩展名混写的 accept */
const extraAcceptMimeByExt: Record<string, string> = {
  mp4: 'video/mp4',
};

/**
 * 将 accept 解析为 MIME 列表（支持 image/jpeg、.png、.mp4 等混写）
 */
const parseAcceptToMimeTypes = (accept: string): string[] => {
  /** 非标准写法 image/jpg 与浏览器 File.type 的 image/jpeg 对齐 */
  const normalizeMimeToken = (mime: string): string => {
    const m = mime.trim().toLowerCase();
    return m === 'image/jpg' ? 'image/jpeg' : m;
  };

  return Array.from(
    new Set(
      accept
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean)
        .flatMap((token) => {
          if (token.includes('/')) {
            return [normalizeMimeToken(token)];
          }
          const ext = token.replace(/^\./, '').toLowerCase();
          const mime = Mimes[ext] || extraAcceptMimeByExt[ext];
          return mime ? [mime] : [];
        }),
    ),
  );
};

/** accept 中是否包含任意 video/* MIME（扩展名 .mp4 或 video/mp4） */
const acceptAllowsVideo = (accept: string): boolean =>
  parseAcceptToMimeTypes(accept).some((m) => m.startsWith('video/'));

const ImageInput = forwardRef((props: ImageInputProps, ref: ForwardedRef<HTMLInputElement>) => {
  const { createImageList } = useTransform();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  let originFiles: File[] = [];

  // 暴露 input 元素的引用和点击方法
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  /**
   * 校验文件大小
   * @param file 文件
   * @param maxSize 最大的大小
   * @param bmpMaxSize BMP 文件最大的大小
   * @returns
   */
  const validateFileSize = (file: File, maxSize: number, bmpMaxSize: number): boolean => {
    if (file.type === 'image/bmp') {
      if (file.size > bmpMaxSize) {
        message.error(`文件 ${file.name} 超过最大允许大小 10MB`);
        return false;
      }
    } else if (file.type === 'video/mp4') {
      if (file.size > 1000 * 1024 * 1024) {
        message.error(`文件 ${file.name} 超过最大允许大小 1000MB`);
        return false;
      }
    } else if (file.size > maxSize) {
      message.error(`文件 ${file.name} 超过最大允许大小 ${maxSize / (1024 * 1024)} MB`);
      return false;
    }

    return true;
  };

  /**
   * 校验视频时长
   * @param file 文件
   * @param duration 最大的时长
   * @returns
   */
  const validateVideoDuration = (file: File, maxDuration: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;
        if (duration > maxDuration) {
          message.error(`视频 ${file.name} 超过最大允许时长 ${maxDuration} 秒`);
          resolve(false);
        } else {
          resolve(true);
        }
      };
      video.onerror = () => {
        reject(new Error('无法读取视频时长'));
      };
      video.src = URL.createObjectURL(file);
    });
  };

  /**
   * 校验图片最大上传数
   * @param files 文件数组
   * @param maxFiles 最大的数量
   * @param count 剩余的数量
   * @returns
   */
  const validateFileCount = (files: FileList, maxFiles: number, count: number): boolean => {
    if (files.length + count > maxFiles) {
      const msg =
        count > 0
          ? `已上传${count},最多还允许上传 ${maxFiles - count} 张图片`
          : `最多只允许上传 ${maxFiles} 张图片`;
      message.error(msg);
      return false;
    }
    return true;
  };

  /**
   * 校验文件类型
   * @param files 文件数组
   * @param accept 接受的文件类型：扩展名（如 .jpeg,.jpg,.bmp,.png,.mp4）与 MIME（如 image/jpeg,video/mp4）可混写
   * @returns
   */
  const validateFileTypes = (files: File[], accept: string): boolean => {
    const acceptedMimeTypes = parseAcceptToMimeTypes(accept);

    // 过滤出符合条件的文件
    const filteredList = files.filter((file) => acceptedMimeTypes.includes(file.type));

    // 如果有文件不符合要求，提示错误
    if (files.length !== filteredList.length) {
      const hintKind = acceptedMimeTypes.some((m) => m.startsWith('video/')) ? '文件' : '图片';
      message.error(`部分文件格式不支持，请上传${accept.replace(/,/g, ' ')} 格式的${hintKind}`);
      return false;
    }

    return true;
  };

  /**
   * 文件转 base64
   * @param file 文件
   * @returns
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as data URL'));
        }
      };
      reader.onerror = (error) => {
        reject(new Error(`File reading error: ${error}`));
      };
    });
  };

  /**
   * 处理所有 PNG 文件并获取处理后的文件数组
   * @param pngList
   * @returns
   */
  const handleAllPngFiles = async (pngList: File[]): Promise<File[]> => {
    const processedFiles: File[] = [];

    await Promise.all(
      pngList.map(async (el: File) => {
        // 1. 读取 PNG 文件为 Data URL
        const base64 = await fileToBase64(el);

        // 2. 创建 Image 对象
        const img: any = new Image();
        img.src = base64;

        // 3. 等待图像加载完成
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // 4. 创建 Canvas 并绘制图像
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          // 5. 从 Canvas 导出为 JPG
          const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9); // 第二个参数是质量，范围 0-1

          // 6. 将 Data URL 转换为 Blob
          const response = await fetch(jpgDataUrl);
          const blob = await response.blob();

          // 7. 创建新的 File 对象
          const newFile = new File([blob], `${el.name.split('.')[0]}.jpg`, { type: 'image/jpeg' });
          processedFiles.push(newFile);
        }
      }),
    );

    // 所有文件处理完成后，返回处理后的文件数组
    return processedFiles;
  };

  /**
   * 处理文件列表（统一的文件处理逻辑）
   * @param files 文件列表
   */
  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      originFiles = fileArray;
      // console.log(files);
      if (!fileArray?.length) {
        return;
      }
      const maxFiles = props?.maxFiles || 50; // 默认最多允许上传5个文件
      const surpluCount = props?.surpluCount || 0; // 剩余可上传图片数量
      const maxSize = props?.maxSize || 20 * 1024 * 1024; // 默认每个文件最大5MB
      const bmpMaxSize = 10 * 1024 * 1024; // BMP 文件最大10MB

      // 验证文件数量
      if (!validateFileCount({ length: fileArray.length } as FileList, maxFiles, surpluCount)) {
        return;
      }

      const fileList: any = fileArray.filter(
        (file: any) => !!file && file.type.indexOf('png') === -1,
      );
      const pngList: any[] = fileArray.filter(
        (file: any) => !!file && file.type.indexOf('png') !== -1,
      );
      const videoList = fileList.filter((item: any) => item.type.indexOf('video') !== -1);

      // 处理所有 PNG 文件并获取处理后的文件数组
      const processedPngFiles = await handleAllPngFiles(pngList);
      // console.log({ fileList, pngList, videoList, processedPngFiles });

      // 找出所有的图片
      const imgList = fileList.filter((item: any) => item.type.indexOf('video') === -1);
      // 处理后的文件
      const list = [...imgList, ...processedPngFiles];
      // console.log({ list });
      // 检查每个文件的大小
      for (const file of [...list, ...videoList]) {
        if (!validateFileSize(file, maxSize, bmpMaxSize)) {
          return;
        }
      }

      // 检查每个视频的时长
      if (videoList.length > 0) {
        const maxDuration = props?.duration || 30; // 最大时长为10秒
        try {
          // 并行验证所有视频的时长
          const validationPromises = videoList.map((file: File) =>
            validateVideoDuration(file, maxDuration),
          );
          // 等待所有验证结果
          const validationResults = await Promise.all(validationPromises);

          // 如果有任何一个视频超过最大时长，显示错误消息并停止上传
          if (validationResults.includes(false)) {
            // message.error('一个或多个视频超过最大允许时长');
            return;
          }
        } catch (error) {
          console.error('Error validating video duration:', error);
          message.error('视频时长验证失败');
          return;
        }
      }

      // eslint-disable-next-line no-debugger
      // debugger;

      // 单个时候，判断是否为视频
      // if (videoList.length > 0) {
      //   props?.onChange?.([], videoList);
      // }

      const acceptStr = props.accept || acceptedMimes;
      // accept 含视频类型时才校验视频，避免默认仅图片的 accept 误拦历史视频上传
      if (videoList.length > 0 && acceptAllowsVideo(acceptStr)) {
        if (!validateFileTypes(videoList, acceptStr)) {
          return;
        }
      }

      if (!validateFileTypes(list, acceptStr)) {
        return;
      }

      if (!props?.onChange) {
        await createImageList(list, true);
      } else {
        // 创建一个映射对象，以便根据 originFiles 的顺序进行排序
        const mapSet = new Map();
        originFiles.forEach((file, index) => mapSet.set(file.name.split('.')[0], index));
        const sortList = list.sort(
          (a, b) => mapSet.get(a.name.split('.')[0]) - mapSet.get(b.name.split('.')[0]),
        );
        // console.log(sortList);
        const res = await createImageList(sortList);
        props?.onChange?.(res, videoList);
      }
    },
    [props, createImageList],
  );

  /**
   * 文件改变事件处理
   * @param event
   */
  const handleChange = useCallback(
    async (event: any) => {
      await processFiles(event.target.files);
      event.target.value = '';
    },
    [processFiles],
  );

  /**
   * 拖拽进入事件处理
   */
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  /**
   * 拖拽离开事件处理
   */
  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // 只有当拖拽真正离开目标元素时才设置为false
      if (!e.relatedTarget || !props.dragTarget?.contains(e.relatedTarget as Node)) {
        setIsDragOver(false);
      }
    },
    [props.dragTarget],
  );

  /**
   * 拖拽悬停事件处理
   */
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  /**
   * 递归读取 FileSystemEntry（文件或文件夹）中的所有文件
   * @param entry FileSystem API 入口
   */
  const readEntryFiles = useCallback((entry: FileSystemEntry): Promise<File[]> => {
    if (entry.isFile) {
      // 普通文件，直接读取
      return new Promise((resolve) => {
        (entry as FileSystemFileEntry).file(
          (file) => resolve([file]),
          () => resolve([]),
        );
      });
    }

    if (entry.isDirectory) {
      // 文件夹，递归读取所有子项
      const dirReader = (entry as FileSystemDirectoryEntry).createReader();
      return new Promise((resolve) => {
        const allEntries: FileSystemEntry[] = [];

        /**
         * readEntries 每次最多返回 100 条，需循环读取直到返回空数组
         */
        const readBatch = () => {
          dirReader.readEntries(
            async (batch) => {
              if (batch.length === 0) {
                // 所有批次读完，递归处理每个子项
                const nested = await Promise.all(allEntries.map(readEntryFiles));
                resolve(nested.flat());
              } else {
                allEntries.push(...batch);
                readBatch();
              }
            },
            () => resolve([]),
          );
        };
        readBatch();
      });
    }

    return Promise.resolve([]);
  }, []);

  /**
   * 拖拽放置事件处理
   * 支持普通文件拖拽和文件夹拖拽（需开启 enableFolderDrag）
   */
  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      // 开启文件夹拖拽：使用 DataTransferItem + webkitGetAsEntry 递归读取
      if (props.enableFolderDrag && e.dataTransfer?.items) {
        const entryPromises = Array.from(e.dataTransfer.items)
          .map((item) => item.webkitGetAsEntry())
          .filter((entry): entry is FileSystemEntry => !!entry)
          .map((entry) => readEntryFiles(entry));

        const nestedFiles = await Promise.all(entryPromises);
        const allFiles = nestedFiles.flat();

        if (allFiles.length > 0) {
          await processFiles(allFiles);
        }
        return;
      }

      // 普通文件拖拽
      const droppedFiles = Array.from(e.dataTransfer?.files || []);
      if (droppedFiles.length > 0) {
        await processFiles(droppedFiles);
      }
    },
    [props.enableFolderDrag, readEntryFiles, processFiles],
  );

  // 设置拖拽事件监听器
  useEffect(() => {
    const dragTarget = props.dragTarget;
    if (!props.enableDrag || !dragTarget) {
      return undefined;
    }

    dragTarget.addEventListener('dragenter', handleDragEnter);
    dragTarget.addEventListener('dragleave', handleDragLeave);
    dragTarget.addEventListener('dragover', handleDragOver);
    dragTarget.addEventListener('drop', handleDrop);

    // 添加拖拽状态的CSS类
    if (isDragOver) {
      dragTarget.classList.add('drag-over');
    } else {
      dragTarget.classList.remove('drag-over');
    }

    return () => {
      dragTarget.removeEventListener('dragenter', handleDragEnter);
      dragTarget.removeEventListener('dragleave', handleDragLeave);
      dragTarget.removeEventListener('dragover', handleDragOver);
      dragTarget.removeEventListener('drop', handleDrop);
      dragTarget.classList.remove('drag-over');
    };
  }, [
    props.enableDrag,
    props.dragTarget,
    isDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  ]);

  return (
    <input
      ref={inputRef}
      className={style.file}
      type="file"
      multiple={props.multiple}
      accept={props?.accept || acceptedMimes}
      onChange={handleChange}
    />
  );
});

export default ImageInput;
